/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {Box, makeStyles, Typography} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Device} from '@ion/core/src/types/entities';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useGetDeviceByDeviceId} from '@ion/query/src/hooks/useGetDeviceByDeviceId';
import CarbTabGrid from './CarbTabGrid';
import CarbTabInfo from './CarbTabInfo';
import useCarbDetails from './useCarbDetails';

interface CarbTabProps {
  tabName?: string;
  tabIndex?: Number;
  currentDevice: Device;
};

export const style = {
  fontFamily    : 'Roboto !important',
  fontWeight    : 500,
  letterSpacing : '0px',
  verticalAlign : 'middle'
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    mainSection : {
      padding : '24px'
    },
    carbDeviceStyle : {
      ...style,
      color      : dlsColor('grey1'),
      fontSize   : '24px !important',
      lineHeight : '28px'
    },
    carbIdentifierStyle : {
      ...style,
      color      : dlsColor('grey1'),
      fontSize   : '16px !important',
      lineHeight : '14px'
    },
    carbGridContainer : {
      paddingTop : '35px',
      height     : 'calc(100vh - 550px)',
      minHeight  : '200px'
    }
  };
});

/**
 * Display detailed information about the CARB and associated device when,
 * CARB service is enabled.
 *
 * ```typescript
 * 
 * <CarbTab
 *   tabName="current"
 *   currentDevice={deviceObject}
 *   tabIndex={11}
 * />
 * 
 * ```
 */
const CarbTab = ({
  tabName,
  tabIndex,
  currentDevice
}: CarbTabProps) => {
  const classes                  = useStyles();
  const {features, id}           = currentDevice || {};
  const {carbRowData, isLoading} = useCarbDetails({currentDevice});
  const {
    result,
    isLoading: isDeviceLoading
  } = useGetDeviceByDeviceId({deviceId: id});
  
  return (
    <div id={`detailsTab_${tabIndex}`}>
      <Box className={classes.mainSection}>
        <Box sx={{p: {xs: 2, sm: 3}, pt: {xs: 2, sm: 3}, mt: 2, bgcolor: dlsColor('white')}}>
          <CarbTabInfo status={features?.CARB || ''}
            serviceEnabledDate={result?.createdOn}
            isDeviceLoading={isDeviceLoading} />
          <div className={classes.carbGridContainer}>
            <CarbTabGrid isLoading={isLoading}
              carbRowData={carbRowData} />
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default CarbTab;
